import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { MsalService } from '.';
import * as Bixby from '$/core';
import { v4 as uuidv4 } from 'uuid';
import { PlannerService } from './PlannerService';

export abstract class PlannerTranscriptionService extends PlannerService {
  public static async addTranscriptionCreateTaskAsync(model: Bixby.Models.NewsTranscriptionDetails): Promise<string> {
    if (!model) {
      throw new Error('Transcription request not found');
    }

    const token: string | null = await MsalService.getPlannerTokenRedirect();

    if (!token || token.length <= 0) {
      throw new Error('Token not found');
    }

    let bucketId = '';
    let planId = '';

    if(!model.hasPrivateDeclaration)
    {
      bucketId = Bixby.Configuration.plannerCallTranscriptPendingBucketId;
      planId = Bixby.Configuration.plannerDataSitePlanId
    }
    else
    {
      bucketId = Bixby.Configuration.plannerMnpiCallTranscriptPendingBucketId;
      planId = Bixby.Configuration.plannerMnpiDataSitePlanId
    }

    const title = `${model.replayGoodFrom} - ${model.bixbyId}`;

    let createPlannerTaskResponse: MicrosoftGraph.PlannerTask;
    try {
      // Create Task
      const createPlannerTask: MicrosoftGraph.PlannerTask = {
        planId: planId,
        bucketId: bucketId,
        title: title,
        assignments: PlannerService.createAssignees({
          assignees: model.assignees,
        } as Bixby.Models.PlannerTask),
      };
      createPlannerTaskResponse = await PlannerService.post<MicrosoftGraph.PlannerTask>(
        token,
        PlannerService.route,
        createPlannerTask
      );
    } catch (createError) {
      throw createError;
    }

    try {
      // Get Task Details
      const getPlannerTaskDetailsResponse: MicrosoftGraph.PlannerTaskDetails =
        await PlannerService.get<MicrosoftGraph.PlannerTaskDetails>(
          token,
          `${PlannerService.route}/${createPlannerTaskResponse.id}/details`
        );

      // Create Checklist
      const checklist = PlannerTranscriptionService.createChecklist(model);
      const initializedChecklist = PlannerService.initializeChecklist(checklist);

      const notes: string =
        `Replay Type:\r\n` +
        `${model.replayType}\r\n\r\n` +
        `Replay ${model.replayType == Bixby.Enums.ReplayType.Phone ? 'Phone Number' : 'Streaming URL'}:\r\n` +
        `${
          model.replayType == Bixby.Enums.ReplayType.Phone
            ? `https://teams.microsoft.com/l/call/0/0?users=4:${model.replayPhoneNumber}`
            : model.replayStreamingUrl
        }\r\n\r\n` +
        `Replay Pin:\r\n` +
        `${model.replayPin || 'No Pin'}\r\n\r\n` +
        `Submit for Approval:\r\n` +
        `${window.location.origin}/admin/transcripts/${model.companyTranscription_Id}/upload`;

      await PlannerService.updateTaskDetails(
        createPlannerTaskResponse.id,
        token,
        (getPlannerTaskDetailsResponse as any)['@odata.etag'],
        initializedChecklist,
        notes
      );

      // Update Checklist Order
      await PlannerService.updateChecklistOrder(createPlannerTaskResponse.id, token);
    } catch (updateError) {
      try {
        await PlannerService.deleteTask(createPlannerTaskResponse.id);
      } catch (deleteError) {
        // TODO - unable to delete task
      }

      // Throw
      throw updateError;
    }

    if (Bixby.Utils.isNullOrEmpty(createPlannerTaskResponse.id)) {
      throw new Error('Invalid planner task ID.');
    }

    return createPlannerTaskResponse.id ?? '';
  }

  public static async addTranscriptionApproveTaskAsync(
    companyId: string,
    companyBixbyId: string,
    transcriptionId: string,
    hasPrivateDeclaration: boolean,
    assignees: string[]
  ): Promise<string> {
    const token: string | null = await MsalService.getPlannerTokenRedirect();

    if (!token || token.length <= 0) {
      throw new Error('Token not found');
    }

    let bucketId = '';
    let planId = '';

    if(!hasPrivateDeclaration)
    {
      bucketId = Bixby.Configuration.plannerCallTranscriptNeedsApprovalBucketId;
      planId = Bixby.Configuration.plannerDataSitePlanId
    }
    else
    {
      bucketId = Bixby.Configuration.plannerMnpiCallTranscriptNeedsApprovalBucketId;
      planId = Bixby.Configuration.plannerMnpiDataSitePlanId
    }
    
    const title = `Transcription Approval - ${companyBixbyId}`;

    let createPlannerTaskResponse: MicrosoftGraph.PlannerTask;
    try {
      // Create Task
      const createPlannerTask: MicrosoftGraph.PlannerTask = {
        planId: planId,
        bucketId: bucketId,
        title: title,
        assignments: PlannerService.createAssignees({
          assignees,
        } as Bixby.Models.PlannerTask),
      };
      createPlannerTaskResponse = await PlannerService.post<MicrosoftGraph.PlannerTask>(
        token,
        PlannerService.route,
        createPlannerTask
      );
    } catch (createError) {
      throw createError;
    }

    try {
      // Get Task Details
      const getPlannerTaskDetailsResponse: MicrosoftGraph.PlannerTaskDetails =
        await PlannerService.get<MicrosoftGraph.PlannerTaskDetails>(
          token,
          `${PlannerService.route}/${createPlannerTaskResponse.id}/details`
        );

      // Create Checklist
      const checklist = PlannerTranscriptionService.approveChecklist(companyBixbyId);
      const initializedChecklist = PlannerService.initializeChecklist(checklist);

      const notes: string =
        `Publish:\r\n` +
        `${window.location.origin}/admin/send-company-update/${companyId}/transcript/${transcriptionId}`;

      await PlannerService.updateTaskDetails(
        createPlannerTaskResponse.id,
        token,
        (getPlannerTaskDetailsResponse as any)['@odata.etag'],
        initializedChecklist,
        notes
      );

      // Update Checklist Order
      await PlannerService.updateChecklistOrder(createPlannerTaskResponse.id, token);
    } catch (updateError) {
      try {
        await PlannerService.deleteTask(createPlannerTaskResponse.id);
      } catch (deleteError) {
        // TODO - unable to delete task
      }

      // Throw
      throw updateError;
    }

    if (Bixby.Utils.isNullOrEmpty(createPlannerTaskResponse.id)) {
      throw new Error('Invalid planner task ID.');
    }

    return createPlannerTaskResponse.id ?? '';
  }

  public static async deleteTranscriptionTaskAsync(id: string | undefined): Promise<void> {
    await PlannerService.deleteTask(id);
  }

  private static createChecklist: (model: Bixby.Models.NewsTranscriptionDetails) => Bixby.Models.ChecklistItem[] = (
    model: Bixby.Models.NewsTranscriptionDetails
  ) => {
    let counter = 1;
    const checklistItems: Bixby.Models.ChecklistItem[] = [];

    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) ${model.client_Name}`),
    });
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) ${model.client_DataSiteEmail}`),
    });

    return checklistItems;
  };

  private static approveChecklist: (companyBixbyId: string) => Bixby.Models.ChecklistItem[] = (
    companyBixbyId: string
  ) => {
    let counter = 1;
    const checklistItems: Bixby.Models.ChecklistItem[] = [];

    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) Audio matches to ${companyBixbyId}`),
    });
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) Text transcript matches to ${companyBixbyId}`),
    });
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) Transcript matches audio`),
    });
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) No Private Information`),
    });
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) Start Date / End Date match lender call invite`),
    });

    return checklistItems;
  };
}
